import React, { useState } from 'react';
import Button from '@/components/UI/Button';
import FormNumberInput from '@/components/UI/FormNumberInput';
import FormSelect from '@/components/UI/FormSelect';
import FormSwitch from '@/components/UI/FormSwitch';
import Modal from '@/components/UI/Modal';

import {
    buildDefaultEntityLimit,
    ENTITY_TYPE_SCOPE_LABELS,
    GOVERNANCE_ENTITY_TYPE_SCOPES,
    type GovernanceEntityLimitDraft
} from './governanceHelpers';

interface GovernanceEntityLimitModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** The row being edited, or null when adding a new scope. */
    existing: GovernanceEntityLimitDraft | null;
    /** Scopes already present in the draft (used to prevent duplicate scope rows when adding). */
    usedScopes: GovernanceEntityLimitDraft['entityTypeScope'][];
    onSave: (limit: GovernanceEntityLimitDraft) => void;
}

const GovernanceEntityLimitModal: React.FC<GovernanceEntityLimitModalProps> = ({ isOpen, onClose, existing, usedScopes, onSave }) => {
    const [draft, setDraft] = useState<GovernanceEntityLimitDraft>(() => existing ?? buildDefaultEntityLimit('All'));

    // Re-seed the form each time the modal opens for a different row, adjusting state during render (guarded
    // by the open key) — React's recommended alternative to a sync effect, and avoids set-state-in-effect.
    const openKey = isOpen ? (existing ? `edit:${existing.entityTypeScope}` : 'add') : null;
    const [seededKey, setSeededKey] = useState<string | null>(null);

    if (openKey !== seededKey) {
        setSeededKey(openKey);

        if (openKey !== null) {
            setDraft(
                existing ?? buildDefaultEntityLimit(GOVERNANCE_ENTITY_TYPE_SCOPES.find((scope) => !usedScopes.includes(scope)) ?? 'All')
            );
        }
    }

    const isEditing = existing !== null;

    // When adding, only scopes not already used are selectable; when editing, the scope is fixed.
    const scopeOptions = GOVERNANCE_ENTITY_TYPE_SCOPES.filter(
        (scope) => scope === draft.entityTypeScope || !usedScopes.includes(scope)
    ).map((scope) => ({ value: scope, label: ENTITY_TYPE_SCOPE_LABELS[scope] }));

    const handleSave = () => {
        onSave(draft);
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            title={isEditing ? `Edit limits — ${ENTITY_TYPE_SCOPE_LABELS[draft.entityTypeScope]}` : 'Add entity-type limits'}
            onClose={onClose}
        >
            <div className="space-y-4">
                <FormSelect
                    disabled={isEditing}
                    helpText="Limits resolve most-specific first: an exact type rule overrides the All rule."
                    id="governanceLimitScope"
                    label="Applies to"
                    options={scopeOptions}
                    value={draft.entityTypeScope}
                    onChange={(e) =>
                        setDraft({ ...draft, entityTypeScope: e.target.value as GovernanceEntityLimitDraft['entityTypeScope'] })
                    }
                />

                <FormNumberInput
                    integer
                    helpText="Total open + counted positions allowed per entity. Blank = no cap."
                    id="governanceLimitMaxPositions"
                    label="Max positions per entity"
                    min={0}
                    placeholder="No cap"
                    value={draft.maxPositionsPerEntity}
                    onValueChange={(value) => setDraft({ ...draft, maxPositionsPerEntity: value })}
                />

                <FormNumberInput
                    integer
                    helpText="Max same-direction trades per entity. Blank = no cap."
                    id="governanceLimitMaxSameDirection"
                    label="Max same-direction trades per entity"
                    min={0}
                    placeholder="No cap"
                    value={draft.maxSameDirectionTradesPerEntity}
                    onValueChange={(value) => setDraft({ ...draft, maxSameDirectionTradesPerEntity: value })}
                />

                <FormNumberInput
                    integer
                    helpText="Max opposite-direction trades per entity. Blank = no cap."
                    id="governanceLimitMaxOppositeDirection"
                    label="Max opposite-direction trades per entity"
                    min={0}
                    placeholder="No cap"
                    value={draft.maxOppositeDirectionTradesPerEntity}
                    onValueChange={(value) => setDraft({ ...draft, maxOppositeDirectionTradesPerEntity: value })}
                />

                <FormNumberInput
                    helpText="Cap on portfolio risk capital concentrated in one entity. Blank = no cap."
                    id="governanceLimitMaxRiskCapitalPercent"
                    label="Max risk capital per entity"
                    min={0}
                    placeholder="No cap"
                    step={0.1}
                    suffix="%"
                    value={draft.maxEntityRiskCapitalPercent}
                    onValueChange={(value) => setDraft({ ...draft, maxEntityRiskCapitalPercent: value })}
                />

                <div className="space-y-2 border-t border-zinc-800 pt-3">
                    <p className="text-sm text-zinc-400">Which existing exposure counts toward these limits:</p>
                    <FormSwitch
                        checked={draft.includePendingOrders}
                        id="governanceLimitIncludePending"
                        label="Count unfilled brokerage orders"
                        onChange={(e) => setDraft({ ...draft, includePendingOrders: e.target.checked })}
                    />
                    <FormSwitch
                        checked={draft.includeQueuedTrades}
                        id="governanceLimitIncludeQueued"
                        label="Count in-flight queued entries"
                        onChange={(e) => setDraft({ ...draft, includeQueuedTrades: e.target.checked })}
                    />
                    <FormSwitch
                        checked={draft.includeManualApprovalPending}
                        id="governanceLimitIncludeManualApproval"
                        label="Count trades awaiting manual approval"
                        onChange={(e) => setDraft({ ...draft, includeManualApprovalPending: e.target.checked })}
                    />
                </div>

                <div className="mt-4 flex justify-end space-x-2">
                    <Button variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSave}>
                        {isEditing ? 'Save limits' : 'Add limits'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default GovernanceEntityLimitModal;
